'use client'

import { reportHandledException } from '@/lib/error-reporting'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ButtonLink, KiaoModal, useKiaoConfirm, toast } from '@kiao-voice/ui-kit'
import { listOwnedPublicNoteShares, revokeOwnedPublicNoteShare } from './api-client'
import type { OwnedPublicNoteShare } from './types'

interface OwnedPublicNoteSharesDialogProps {
  open: boolean
  onClose: () => void
  listSharesFn?: typeof listOwnedPublicNoteShares
  revokeShareFn?: typeof revokeOwnedPublicNoteShare
  buildShareUrl?: (token: string) => string
}

function defaultShareUrl(token: string): string {
  return `${window.location.origin}/share/${token}`
}

export function OwnedPublicNoteSharesDialog({
  open,
  onClose,
  listSharesFn = listOwnedPublicNoteShares,
  revokeShareFn = revokeOwnedPublicNoteShare,
  buildShareUrl = defaultShareUrl,
}: OwnedPublicNoteSharesDialogProps) {
  const { t } = useTranslation('web')
  const confirm = useKiaoConfirm()
  const [phase, setPhase] = useState<'loading' | 'ready' | 'error'>('loading')
  const [shares, setShares] = useState<OwnedPublicNoteShare[]>([])
  const [busyToken, setBusyToken] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setPhase('loading')
    listSharesFn()
      .then((result) => {
        if (cancelled) return
        setShares(result)
        setPhase('ready')
      })
      .catch((error) => {
        reportHandledException(error, {
          source: 'features/meeting-notes/owned-public-note-shares-dialog.tsx',
        })
        if (cancelled) return
        console.error('[public-note-share] could not load owned links', error)
        setPhase('error')
      })
    return () => {
      cancelled = true
    }
  }, [listSharesFn, open])

  const handleRevoke = async (share: OwnedPublicNoteShare) => {
    const approved = await confirm({
      title: t('share.managerRevokeTitle'),
      message: t('share.managerRevokeBody'),
      confirmText: t('share.revoke'),
      cancelText: t('share.cancel'),
    })
    if (!approved || busyToken) return

    setBusyToken(share.token)
    try {
      await revokeShareFn(share.token)
      setShares((current) => current.filter((candidate) => candidate.token !== share.token))
      toast.success(t('share.revoked'))
    } catch (error) {
      reportHandledException(error, {
        source: 'features/meeting-notes/owned-public-note-shares-dialog.tsx',
      })
      console.error('[public-note-share] could not revoke owned link', error)
      toast.error(t('share.actionFailed'))
    } finally {
      setBusyToken(null)
    }
  }

  return (
    <KiaoModal
      open={open}
      onClose={onClose}
      title={t('share.managerTitle')}
      subtitle={t('share.managerSubtitle')}
      closeLabel={t('share.close')}
      className="kiao-modal--public-share"
    >
      <div className="pns">
        {phase === 'loading' ? <p className="pns-status">{t('share.managerLoading')}</p> : null}
        {phase === 'error' ? <p className="pns-status">{t('share.managerLoadFailed')}</p> : null}
        {phase === 'ready' && shares.length === 0 ? (
          <p className="pns-empty">{t('share.managerEmpty')}</p>
        ) : null}
        {phase === 'ready' && shares.length > 0 ? (
          <ul className="grid list-none gap-3 p-0">
            {shares.map((share) => (
              <li
                key={share.token}
                className="rounded-xl border border-[var(--border)] bg-white p-4"
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="truncate text-base font-semibold text-[var(--fg)]">
                      {share.title}
                    </h3>
                    <p className="mt-1 text-sm text-[var(--muted)]">
                      {[
                        share.templateName,
                        share.publishedAt
                          ? new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(
                              new Date(share.publishedAt)
                            )
                          : null,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </p>
                    {!share.sourceAvailable ? (
                      <p className="mt-2 text-sm text-[var(--muted)]">
                        {t('share.managerSourceExpired')}
                      </p>
                    ) : null}
                  </div>
                  <div className="flex shrink-0 flex-wrap gap-2">
                    <ButtonLink
                      variant="secondary"
                      size="sm"
                      href={buildShareUrl(share.token)}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {t('share.managerOpen')}
                    </ButtonLink>
                    <button
                      className="btn btn-secondary btn-sm"
                      type="button"
                      disabled={busyToken !== null}
                      onClick={() => void handleRevoke(share)}
                    >
                      {t('share.revoke')}
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </KiaoModal>
  )
}
